import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getUserBasedQuestions } from '../api/infiniumChatApi';
import { WEBCHAT_CONFIG } from './webchatConfig';
import { preprocessMessage } from '../utils/textFormatting';

const MyQuestions = ({ isDarkMode }) => {
    const [questions, setQuestions] = useState([]);
    const [selectedQuestion, setSelectedQuestion] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchQuestions = async () => {
            setIsLoading(true);
            try {
                // Get userId from localStorage (same as ChatHistory/UseWebChat)
                const storedSession = localStorage.getItem(WEBCHAT_CONFIG.clientName);
                let userId = null;

                if (storedSession) {
                    const session = JSON.parse(storedSession);
                    userId = session.userId;
                }

                if (!userId) {
                    // Fallback or try to get from user object if available in props/context?
                    // For now, if no userId, we can't fetch.
                    console.warn("No userId found for fetching questions.");
                    setIsLoading(false);
                    return;
                }

                const response = await getUserBasedQuestions(userId);

                if (response.success && response.data && response.data.updatedQuestion) {
                    const apiQuestions = response.data.updatedQuestion.map(q => ({
                        id: q._id,
                        status: q.answered ? 'Replied' : 'Pending',
                        timestamp: q.createdAt,
                        question: q.question,
                        botResponseContent: q.questionFor,
                        adminReply: q.answer,
                        replyTimestamp: q.answeredAt,
                        answeredBy: q.answeredBy
                    }));

                    // Sort by timestamp desc
                    setQuestions(apiQuestions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
                } else {
                    // If API fails or returns no data, we might want to leave questions empty or show error
                    console.error("Failed to fetch questions:", response.error);
                    setError("Failed to load your questions.");
                }
            } catch (err) {
                console.error("Error in fetchQuestions:", err);
                setError("An error occurred while loading questions.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Replied': return isDarkMode ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-green-100 text-green-700 border-green-200';
            case 'Pending': return isDarkMode ? 'bg-yellow-900/30 text-yellow-400 border-yellow-800' : 'bg-yellow-100 text-yellow-700 border-yellow-200';
            default: return isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-700';
        }
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'
        });
    };

    // Detail View
    if (selectedQuestion) {
        return (
            <div className={`flex-1 overflow-hidden flex flex-col h-full ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}>
                {/* Header */}
                <div className={`p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} flex items-center gap-4`}>
                    <button
                        onClick={() => setSelectedQuestion(null)}
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Question Details
                        </h2>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            ID: {selectedQuestion.id.slice(-8)}
                        </p>
                    </div>
                    <div className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(selectedQuestion.status)}`}>
                        {selectedQuestion.status}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* 1. Bot Response Context */}
                    <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-cyan-500' : 'text-cyan-600'}`}>
                            Regarding Bot Response
                        </h3>
                        <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert text-gray-300' : 'text-gray-700'}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {preprocessMessage(selectedQuestion.botResponseContent)}
                            </ReactMarkdown>
                        </div>
                    </div>

                    {/* 2. User Question */}
                    <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-bold shrink-0 shadow-md">
                            Q
                        </div>
                        <div className="flex-1">
                            <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                You asked • {formatDate(selectedQuestion.timestamp)}
                            </div>
                            <div className={`p-4 rounded-2xl rounded-tl-none shadow-sm ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white border border-gray-100 text-gray-900'}`}>
                                <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {preprocessMessage(selectedQuestion.question)}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Admin Reply */}
                    {selectedQuestion.adminReply && (Array.isArray(selectedQuestion.adminReply) ? selectedQuestion.adminReply.length > 0 : true) ? (
                        <div className="space-y-6">
                            {(Array.isArray(selectedQuestion.adminReply) ? selectedQuestion.adminReply : [selectedQuestion.adminReply]).map((reply, index) => (
                                <div key={index} className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold shrink-0 shadow-md">
                                        A{Array.isArray(selectedQuestion.adminReply) && selectedQuestion.adminReply.length > 1 ? index + 1 : ''}
                                    </div>
                                    <div className="flex-1">
                                        <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Admin replied • {selectedQuestion.replyTimestamp ? formatDate(selectedQuestion.replyTimestamp) : 'Recently'}
                                        </div>
                                        <div className={`p-4 rounded-2xl rounded-tl-none shadow-sm border ${isDarkMode ? 'bg-purple-900/20 border-purple-800 text-purple-100' : 'bg-purple-50 border-purple-100 text-gray-900'
                                            }`}>
                                            <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert text-purple-100' : 'text-gray-900'}`}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {preprocessMessage(reply)}
                                                </ReactMarkdown>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={`flex items-center justify-center p-8 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-gray-800 text-gray-600' : 'border-gray-200 text-gray-400'}`}>
                            <p>Waiting for admin reply...</p>
                        </div>
                    )}

                </div>
            </div>
        );
    }

    // List View
    return (
        <div className="flex-1 overflow-hidden flex flex-col h-full">
            <div className="p-8 pb-4">
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    My Questions
                </h1>
                <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Track your queries about AI responses.
                </p>
            </div>

            <div className="flex-1 overflow-y-auto px-8 pb-8">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mb-4"></div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>Loading your questions...</p>
                    </div>
                ) : error ? (
                    <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-red-900/30 text-red-400 border-red-800' : 'bg-red-100 text-red-700 border-red-200'}`}>
                        {error}
                    </div>
                ) : questions.length === 0 ? (
                    <div className={`flex flex-col items-center justify-center h-64 rounded-2xl border-2 border-dashed ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                        <div className={`p-4 rounded-full mb-4 flex items-center gap-1 ${isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400'}`}>
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="8" r="5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                <path d="M20 21a8 8 0 0 0-16 0" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            </svg>
                            <svg className="w-8 h-8 -ml-2 mt-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                            </svg>
                        </div>
                        <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>No questions asked yet</p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {questions.map((q) => (
                            <div
                                key={q.id}
                                onClick={() => setSelectedQuestion(q)}
                                className={`group p-5 rounded-2xl border transition-all cursor-pointer hover:shadow-md ${isDarkMode
                                    ? 'bg-gray-900 border-gray-800 hover:border-gray-700'
                                    : 'bg-white border-gray-200 hover:border-cyan-200'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(q.status)}`}>
                                        {q.status}
                                    </span>
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {formatDate(q.timestamp)}
                                    </span>
                                </div>
                                <h3 className={`font-semibold text-lg mb-2 line-clamp-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {q.question}
                                </h3>
                                <div className={`text-sm line-clamp-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    Regarding: {q.botResponseContent}
                                </div>
                                <div className={`mt-4 flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-cyan-400 group-hover:text-cyan-300' : 'text-cyan-600 group-hover:text-cyan-700'}`}>
                                    View Details
                                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyQuestions;
